import {computed, reactive, ref, Ref, shallowReactive, toValue, watch} from 'vue'
import {createSharedComposable, useDevicePixelRatio, useResizeObserver} from '@vueuse/core'
import type {MaybeRefOrGetter} from '@vueuse/shared'
import {
  IFileVariantResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IFileVariantResponseInstance'

import {useImage} from '@toolify/client/src/modules/ui/composables/useImage'
import {ImageLoadState} from '@toolify/client/src/modules/ui/enum/ImageLoadState'
import {getBestImageFileVariant} from '@toolify/client/src/modules/ui/functions/getBestImageFileVariant'
import {filterImageFileVariants} from '@toolify/client/src/modules/ui/functions/filterImageFileVariants'

interface IBestImageOptions {
  maxAreaDifference?: number
  fileVariants?: IFileVariantResponseInstance[]
  url?: string
}

const loadedImageUrls = reactive(new Set<string>())
const lastLoadedUrlByImageKey = shallowReactive(new Map<string, string>())

const isCacheableUrl = (url: string): boolean => !url.startsWith('data:') && !url.startsWith('blob:')

const useSharedDevicePixelRatio = createSharedComposable(useDevicePixelRatio)

export function useBestImage(
  elementToDetermineSizeRef: Ref<HTMLElement>,
  optionsRef?: MaybeRefOrGetter<IBestImageOptions>,
) {
  const {pixelRatio} = useSharedDevicePixelRatio()
  // The size comes only from the observer, which reports after layout: reading it on mount forces a
  // layout per image. It also only grows, because swapping the rendered child (skeleton, img, fallback)
  // can resize the element, and following every shrink would pick another variant and reload forever.
  const requestedWidth = ref(0)
  const requestedHeight = ref(0)
  useResizeObserver(elementToDetermineSizeRef, ([entry]) => {
    const {width, height} = entry.contentRect
    if(!width || !height || (width <= requestedWidth.value && height <= requestedHeight.value)) {
      return
    }
    requestedWidth.value = Math.max(requestedWidth.value, width)
    requestedHeight.value = Math.max(requestedHeight.value, height)
  })
  const options = computed<IBestImageOptions>(() => toValue(optionsRef) ?? {})
  const isElementMeasured = computed(() => !!requestedWidth.value && !!requestedHeight.value)
  const imageFileVariants = computed(() => filterImageFileVariants(options.value.fileVariants))
  const imageKey = computed<string | null>(() => imageFileVariants.value.find(fileVariant => !!fileVariant.url)?.url ?? null)
  const lastLoadedUrl = computed<string | null>(() => {
    const url = imageKey.value ? lastLoadedUrlByImageKey.get(imageKey.value) : null
    return url && loadedImageUrls.has(url) ? url : null
  })
  const bestThumbnailVariant = computed(() => {
    return getBestImageFileVariant(
      options.value.fileVariants,
      requestedWidth.value,
      requestedHeight.value,
      pixelRatio.value,
      options.value.maxAreaDifference,
    )
  })
  const requestedUrl = computed<string | null>(() => {
    if(bestThumbnailVariant.value) {
      return bestThumbnailVariant.value.url
    }
    if(options.value.url) {
      return options.value.url
    }
    if(!isElementMeasured.value) {
      return lastLoadedUrl.value
    }
    return null
  })
  const {state: loadedImage, isLoading, error} = useImage(() => {
    return {
      src: requestedUrl.value,
    }
  })

  watch(loadedImage, (image) => {
    const src = image?.getAttribute('src')
    if(!src || !isCacheableUrl(src)) {
      return
    }
    loadedImageUrls.add(src)
    if(imageKey.value && imageFileVariants.value.some(fileVariant => fileVariant.url === src)) {
      lastLoadedUrlByImageKey.set(imageKey.value, src)
    }
  })

  watch(error, (loadError) => {
    const failedImage = (loadError as Event | undefined)?.target
    const failedSrc = failedImage instanceof HTMLImageElement ? failedImage.getAttribute('src') : null
    if(failedSrc) {
      loadedImageUrls.delete(failedSrc)
    }
  })

  const bestFileVariantUrl = computed<string | null>(() => {
    const url = requestedUrl.value
    if(url && !loadedImageUrls.has(url) && lastLoadedUrl.value) {
      return lastLoadedUrl.value
    }
    return url
  })

  const loadState = computed<ImageLoadState>(() => {
    if(!bestFileVariantUrl.value) {
      return !isElementMeasured.value && !!imageKey.value
        ? ImageLoadState.Loading
        : ImageLoadState.Failed
    }
    if(loadedImageUrls.has(bestFileVariantUrl.value)) {
      return ImageLoadState.Loaded
    }
    if(isLoading.value) {
      return ImageLoadState.Loading
    }
    if(error.value) {
      return ImageLoadState.Failed
    }
    return ImageLoadState.Loaded
  })

  return {
    bestFileVariantUrl,
    loadState,
  }
}
